const DOM_GUARDS_FLAG = "__akunaDomGuardsApplied";

export const isDomMutationError = (error: unknown) => {
  if (!error) return false;

  if (error instanceof DOMException) {
    if (error.name === "NotFoundError") return true;
  }

  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes("removeChild") ||
    message.includes("insertBefore") ||
    message.includes("not a child of this node")
  );
};

export const isResizeObserverLoopError = (error: unknown) => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "object" && error !== null && "message" in error
        ? String(error.message)
        : String(error ?? "");
  return message === "ResizeObserver loop completed with undelivered notifications";
};

// react-big-calendar's MonthView debounces slot selection via a bare setTimeout.
// If that deferred callback fires after a duplicate/racing select event has
// already emptied its pending-selection queue (seen on fast/duplicate touch
// taps), it reads `.getDate()` off `undefined` before ever calling our
// onSelectSlot handler — there is nothing app-side to intercept, and no newer
// published version (currently 1.20.0, latest) fixes it upstream.
export const isCalendarSelectionRaceError = (event: unknown) => {
  const message =
    event instanceof ErrorEvent
      ? event.message
      : event instanceof Error
        ? event.message
        : String(event ?? "");
  if (!message.includes("reading 'getDate'")) return false;

  const innerError = event instanceof ErrorEvent ? event.error : event;
  const stack = innerError instanceof Error ? innerError.stack ?? "" : "";
  return stack.includes("react-big-calendar");
};

export const applyDomGuards = () => {
  if (typeof window === "undefined") return;
  const win = window as typeof window & { [DOM_GUARDS_FLAG]?: boolean };
  if (win[DOM_GUARDS_FLAG]) return;
  win[DOM_GUARDS_FLAG] = true;

  window.addEventListener(
    "error",
    (event) => {
      if (isResizeObserverLoopError(event) || isCalendarSelectionRaceError(event)) {
        event.preventDefault();
      }
    },
    true,
  );

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildPatched<T extends Node>(child: T) {
    if (child && child.parentNode !== this) {
      return child;
    }

    try {
      return originalRemoveChild.call(this, child);
    } catch (error) {
      if (isDomMutationError(error)) {
        return child;
      }
      throw error;
    }
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforePatched<T extends Node>(
    newChild: T,
    referenceNode: Node | null,
  ) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newChild;
    }

    try {
      return originalInsertBefore.call(this, newChild, referenceNode);
    } catch (error) {
      if (isDomMutationError(error)) {
        return newChild;
      }
      throw error;
    }
  };
};
